import React, { useState, useEffect } from 'react';
import { Card, Table, Button, Badge, Form, Alert, Row, Col } from 'react-bootstrap';
import { useUsers } from '@hooks';
import { useAuth } from '@context';
import { canManageCourse, isAdmin } from '@utils/permissions';

const normalizeOrganizationKey = (value) => String(value || '').trim().toLowerCase();

/**
 * Course Enrollment Manager Component
 * Manages user enrollments for a specific course
 */
export function CourseEnrollmentManager({ courseId, courseName, onBack }) {
  const {
    users,
    enrollUser,
    unenrollUser,
    createUser,
    suspendUserInCourse,
    unsuspendUserInCourse,
    loading: usersLoading,
  } = useUsers();
  const { user } = useAuth();
  const userIsAdmin = isAdmin(user);
  const managerOrganizationKey = normalizeOrganizationKey(user?.organizationName);
  const managerOrganizationMissing = !userIsAdmin && !managerOrganizationKey;
  const [enrolledUsers, setEnrolledUsers] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [createFormData, setCreateFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  /**
   * Update enrolled and available users lists
   */
  useEffect(() => {
    if (users.length > 0 && courseId) {
      const managedCourseIds = Array.isArray(user?.managedCourses) ? user.managedCourses : [];

      const visibleUsers = userIsAdmin
        ? users
        : users.filter((candidate) => {
          const candidateOrganizationKey = normalizeOrganizationKey(candidate.organizationName);
          if (managerOrganizationKey && candidateOrganizationKey && candidateOrganizationKey !== managerOrganizationKey) {
            return false;
          }

          const candidateCourses = Array.isArray(candidate.enrolledCourses) ? candidate.enrolledCourses : [];
          const enrolledInManagedCourse = candidateCourses.some((managedCourseId) => managedCourseIds.includes(managedCourseId));
          const addedByManager = candidate.createdByUserId === user?.id;
          return enrolledInManagedCourse || addedByManager;
        });

      const students = visibleUsers.filter(u => u.role === 'student');
      const enrolled = students.filter(u => (u.enrolledCourses || []).includes(courseId));
      const available = students.filter(u => !(u.enrolledCourses || []).includes(courseId));
      setEnrolledUsers(enrolled);
      setAvailableUsers(available);
      return;
    }

    setEnrolledUsers([]);
    setAvailableUsers([]);
  }, [users, courseId, user?.managedCourses, user?.id, userIsAdmin, managerOrganizationKey]);

  if (!canManageCourse(user, courseId)) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="danger" className="mb-0">Access denied: You are not assigned to this course.</Alert>
        </Card.Body>
      </Card>
    );
  }

  if (managerOrganizationMissing) {
    return (
      <Card>
        <Card.Body>
          <Alert variant="warning" className="mb-0">
            Your course manager account must be assigned to an organization before you can manage learners.
          </Alert>
        </Card.Body>
      </Card>
    );
  }

  /**
   * Handle enrolling a user
   */
  const handleEnroll = async (e) => {
    e.preventDefault();
    if (!selectedUser) {
      setError('Please select a user to enroll');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      const sendNotificationEmail = window.confirm('Do you want to send a notification email for this enrollment?');
      await enrollUser(selectedUser, courseId, { sendNotificationEmail });
      setSuccess(
        sendNotificationEmail
          ? 'User enrolled successfully and notification email was requested.'
          : 'User enrolled successfully without sending a notification email.'
      );
      setSelectedUser('');
    } catch (err) {
      setError(`Failed to enroll user: ${err.message}`);
    }
  };

  /**
   * Handle creating and enrolling a new student in this course
   */
  const handleCreateAndEnroll = async (event) => {
    event.preventDefault();

    const firstName = String(createFormData.firstName || '').trim();
    const lastName = String(createFormData.lastName || '').trim();
    const email = String(createFormData.email || '').trim().toLowerCase();
    const fullName = `${firstName} ${lastName}`.trim();

    if (!firstName) {
      setError('First name is required');
      return;
    }

    if (!lastName) {
      setError('Last name is required');
      return;
    }

    if (!email) {
      setError('Email is required');
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setError('Please enter a valid email address');
      return;
    }

    try {
      setError(null);
      setSuccess(null);
      setCreateLoading(true);

      const sendNotificationEmail = window.confirm('Do you want to send a notification email to this new student?');

      const createdUser = await createUser({
        firstName,
        lastName,
        name: fullName,
        email,
        role: 'student',
        organizationName: user?.organizationName || null,
        isActive: true,
        enrolledCourses: [courseId],
        managedCourses: [],
        createdByUserId: user?.id || null,
      }, { sendNotificationEmail });

      if (!createdUser?.id) {
        throw new Error('Failed to create user');
      }

      setCreateFormData({ firstName: '', lastName: '', email: '' });
      setSuccess(
        sendNotificationEmail
          ? 'Student enrollment processed successfully. Notification email(s) were requested.'
          : 'Student enrollment processed successfully without sending notification emails.'
      );
    } catch (err) {
      setError(`Failed to process enrollment: ${err.message}`);
    } finally {
      setCreateLoading(false);
    }
  };

  /**
   * Handle unenrolling a user
   */
  const handleUnenroll = async (userId, userName) => {
    if (window.confirm(`Are you sure you want to unenroll ${userName} from this course?`)) {
      try {
        setError(null);
        setSuccess(null);
        await unenrollUser(userId, courseId);
        setSuccess('User unenrolled successfully');
      } catch (err) {
        setError(`Failed to unenroll user: ${err.message}`);
      }
    }
  };

  /**
   * Handle course-level suspension toggle
   */
  const handleToggleCourseSuspension = async (targetUser, isSuspendedInCourse) => {
    const actionLabel = isSuspendedInCourse ? 'unsuspend' : 'suspend';
    const confirmationMessage = isSuspendedInCourse
      ? `Unsuspend ${targetUser.name} in this course?`
      : `Suspend ${targetUser.name} in this course?`;

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setError(null);
      setSuccess(null);

      if (isSuspendedInCourse) {
        await unsuspendUserInCourse(targetUser.id, courseId);
        setSuccess(`${targetUser.name} was unsuspended in this course.`);
      } else {
        await suspendUserInCourse(targetUser.id, courseId);
        setSuccess(`${targetUser.name} was suspended in this course.`);
      }
    } catch (err) {
      setError(`Failed to ${actionLabel} user in course: ${err.message}`);
    }
  };

  if (usersLoading) {
    return <div className="text-center py-4">Loading...</div>;
  }

  return (
    <div>
      {onBack && (
        <div className="mb-3">
          <Button variant="link" onClick={onBack}>← Back to Users</Button>
        </div>
      )}

      <Card className="mb-3">
        <Card.Header>
          <h5 className="mb-0">Course Enrollments: {courseName}</h5>
        </Card.Header>
        <Card.Body>
          {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}
          {success && <Alert variant="success" dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

          {/* Enroll New User Form */}
          <Card className="mb-3 bg-light">
            <Card.Body>
              <h6>Enroll New User</h6>
              <Form onSubmit={handleEnroll}>
                <Row className="align-items-end">
                  <Col md={8}>
                    <Form.Group>
                      <Form.Label>Select User</Form.Label>
                      <Form.Select
                        value={selectedUser}
                        onChange={(e) => setSelectedUser(e.target.value)}
                      >
                        <option value="">Choose a user...</option>
                        {availableUsers.map(user => (
                          <option key={user.id} value={user.id}>
                            {user.name} ({user.email})
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                  <Col md={4}>
                    <Button 
                      type="submit" 
                      variant="primary" 
                      disabled={!selectedUser}
                      className="w-100"
                    >
                      Enroll User
                    </Button>
                  </Col>
                </Row>
              </Form>
              {availableUsers.length === 0 && (
                <small className="text-muted">No eligible users are available to enroll in this course.</small>
              )}
            </Card.Body>
          </Card>

          {!userIsAdmin && (
            <Card className="mb-3 bg-light">
              <Card.Body>
                <h6>Create and Enroll New Student</h6>
                <Form onSubmit={handleCreateAndEnroll}>
                  <Row className="g-3 align-items-end">
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={createFormData.firstName}
                          onChange={(event) => setCreateFormData((prev) => ({ ...prev, firstName: event.target.value }))}
                          placeholder="First name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Last Name</Form.Label>
                        <Form.Control
                          type="text"
                          value={createFormData.lastName}
                          onChange={(event) => setCreateFormData((prev) => ({ ...prev, lastName: event.target.value }))}
                          placeholder="Last name"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Email</Form.Label>
                        <Form.Control
                          type="email"
                          value={createFormData.email}
                          onChange={(event) => setCreateFormData((prev) => ({ ...prev, email: event.target.value }))}
                          placeholder="student@example.com"
                        />
                      </Form.Group>
                    </Col>
                    <Col md={12}>
                      <Button
                        type="submit"
                        variant="primary"
                        disabled={createLoading}
                      >
                        {createLoading ? 'Creating...' : 'Create Student in This Course'}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              </Card.Body>
            </Card>
          )}

          {/* Enrolled Users List */}
          <h6>Enrolled Users ({enrolledUsers.length})</h6>
          {enrolledUsers.length === 0 ? (
            <div className="text-center py-4 text-muted">
              No users enrolled in this course yet.
            </div>
          ) : (
            <Table striped bordered hover responsive>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Role</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {enrolledUsers.map(user => (
                  <tr key={user.id}>
                    <td>{user.name}</td>
                    <td>{user.email}</td>
                    <td>
                      <Badge bg="primary">
                        {user.role}
                      </Badge>
                    </td>
                    <td>
                      {(() => {
                        const accountActive = user.isActive !== false;
                        const isSuspendedInCourse = (user.suspendedCourses || []).includes(courseId);
                        const badgeVariant = !accountActive
                          ? 'secondary'
                          : isSuspendedInCourse
                            ? 'warning'
                            : 'success';
                        const statusLabel = !accountActive
                          ? 'Account inactive'
                          : isSuspendedInCourse
                            ? 'Suspended in course'
                            : 'Active in course';

                        return (
                          <Badge bg={badgeVariant}>
                            {statusLabel}
                          </Badge>
                        );
                      })()}
                    </td>
                    <td>
                      {(() => {
                        const accountActive = user.isActive !== false;
                        const isSuspendedInCourse = (user.suspendedCourses || []).includes(courseId);
                        return (
                          <Button
                            variant={isSuspendedInCourse ? 'outline-success' : 'outline-warning'}
                            size="sm"
                            className="me-2"
                            onClick={() => handleToggleCourseSuspension(user, isSuspendedInCourse)}
                            disabled={!accountActive}
                            title={!accountActive ? 'Activate account to manage course suspension' : undefined}
                          >
                            {isSuspendedInCourse ? 'Unsuspend' : 'Suspend'}
                          </Button>
                        );
                      })()}
                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() => handleUnenroll(user.id, user.name)}
                      >
                        Unenroll
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>
    </div>
  );
}
